import { Box, CircularProgress } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendNotifications } from '../../../../apis/sendNotification';
import { sendReceipt } from '../../../../apis/sendReceipt';
import ALIGN_LEFT_ICON from '../../../../assets/svg/align-left-icon.svg';
import Button from '../../../../components/Button/Button';
import Modal from '../../../../components/Modal/Modal';
import Toast from '../../../../components/Toast/Toast';
import db_config from '../../../../configs/db_config';
import { LANG_TEXT } from '../../../../constants/language';
import { DELIVERY_FEE } from '../../../../consts/language';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { useOrderContext } from '../../../../context/OrderContext';
import { useOrderStadiumContext } from '../../../../context/OrderStadiumContext';
import { useSettingsContext } from '../../../../context/SettingsContext';
import useCreateRefund from '../../../../hooks/useCreateRefund';
import usePermission from '../../../../hooks/usePermission';
import { DamagedItemStatus, OrderItemStatus } from '../../../../interfaces/index.types';
import { db } from '../../../../libs/firebase';
import { doc, getDoc } from '../../../../libs/firestore-wrapper';
import { IFood, IOrders } from '../../order.types';
import { buttonBackStyle, buttonStyle, textButton, useStyles } from '../DrawerOrderItem/styles';

interface OrderCancellationProps {
  setCancellationOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setPartialCancellation: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDrawer?: ((open: boolean) => void) | undefined;
  updatePromotionOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateRegularOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateOrderStatus?: ((orderId: string, status: string, updatedFoods?: IFood[]) => Promise<void>) | undefined;
  order?: IOrders;
  restaurantId?: string;
}

const OrderCancellation: React.FC<OrderCancellationProps> = ({
  setCancellationOrder,
  setPartialCancellation,
  toggleDrawer,
  updatePromotionOrderStatus,
  updateRegularOrderStatus,
  updateOrderStatus,
  order,
  restaurantId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openModalCancellation, setOpenModalCancellation] = useState<boolean>(false);
  const { updateOrderStatusDamaged, allOrders: resORders } = useOrderContext();
  const { allOrders: stadiumOrders } = useOrderStadiumContext();

  const handleOpenModalCancellation = () => setOpenModalCancellation(true);
  const handleCloseModalCancellation = () => setOpenModalCancellation(false);
  const { mutateAsync } = useCreateRefund();
  const { stadiumData } = useSettingsContext();
  const { userInfo } = useAuthContext();
  const { showForSuperAdmin, showForAdmin } = usePermission();

  const allOrders = useMemo(() => {
    if (showForSuperAdmin || showForAdmin) {
      return stadiumOrders;
    }

    return resORders;
  }, [resORders, stadiumOrders, showForSuperAdmin, showForAdmin]);

  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });
  const [openToast, setOpenToast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBillData = async (orderId: string) => {
    const snapshot = await getDoc(doc(db, db_config.DB_STADIUM_BILLS, orderId));
    const billData = snapshot.data()?.data?.object;
    if (!billData) throw new Error('Bill data not found');
    return billData;
  };

  const computeTotalPrice = (foods: IFood[]) => {
    const foodsToCalculate = restaurantId ? foods.filter((food) => food.restaurantId === restaurantId) : foods;
    return foodsToCalculate.reduce((total, food) => {
      const quantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
      return total + quantity * food.item_price;
    }, 0);
  };

  const notifyAndUpdateStatus = async (order: IOrders, status: string, updatedFoods: IFood[]) => {
    await updateOrderStatus?.(order.id, status, updatedFoods);
    if (order.promotion_foods?.length) {
      await (order.type === 'promotion' ? updatePromotionOrderStatus?.(order.id, status) : updateRegularOrderStatus?.(order.id, status));
    }
    if (order.rider_id) {
      const parent_id = order?.parent_id ?? order.id;
      await sendNotifications(order.rider_id, { id: parent_id, status });
    }
  };

  const handleConfirmFullCancellation = async (order: IOrders) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const statusCancelled = OrderItemStatus.CANCELLED;
      const statusDamagedCancelled = DamagedItemStatus.DAMAGED_CANCELLED;

      if (!order.is_damaged) {
        const billData = await fetchBillData(order.id);
        const totalPrice = computeTotalPrice(order.foods);

        // Multi-order: only refund delivery fee when ALL restaurants have cancelled
        const isFullOrderCancelled = (() => {
          if (!order.multi_orders || order.multi_orders.length === 0) return true;
          const otherOrders = order.multi_orders.filter((o) => o.restaurant_id !== restaurantId);
          return otherOrders.every((o) => o.status === OrderItemStatus.CANCELLED || o.status === DamagedItemStatus.DAMAGED_CANCELLED);
        })();

        await mutateAsync({
          chargeId: billData.id,
          amount: totalPrice + (isFullOrderCancelled ? DELIVERY_FEE : 0),
          reason: 'cancellation',
          stadiumID: stadiumData.id || userInfo.stadium_id || '',
        });

        const updatedFoods =
          order.all_foods?.map((food) => ({
            ...food,
            item_deleted_quantity: restaurantId && food.restaurantId !== restaurantId ? food.item_deleted_quantity || 0 : food.item_quantity,
          })) || [];

        await notifyAndUpdateStatus(order, statusCancelled, updatedFoods);
        try {
          await sendReceipt(order.id, 'cancel');
        } catch (e) {}
      } else if (order.parent_id) {
        const orderOrigin = allOrders.find((i) => i.id === order.parent_id);
        if (orderOrigin) {
          const billData = await fetchBillData(orderOrigin.id);
          const totalPrice = computeTotalPrice(orderOrigin.foods);

          const isFullOrderCancelledDamaged = (() => {
            if (!orderOrigin.multi_orders || orderOrigin.multi_orders.length === 0) return true;
            const otherOrders = orderOrigin.multi_orders.filter((o) => o.restaurant_id !== restaurantId);
            return otherOrders.every((o) => o.status === OrderItemStatus.CANCELLED || o.status === DamagedItemStatus.DAMAGED_CANCELLED);
          })();

          await mutateAsync({
            chargeId: billData.id,
            amount: totalPrice + (isFullOrderCancelledDamaged ? DELIVERY_FEE : 0),
            reason: 'cancellation',
            stadiumID: stadiumData.id || userInfo.stadium_id || '',
          });

          const updatedFoods =
            orderOrigin.all_foods?.map((food) => {
              const _food = order.foods.find((f) => f.item_food_id === food.item_food_id);
              const deletedQuantity = food.restaurantId === restaurantId ? _food?.item_quantity : food.item_deleted_quantity || 0;

              return {
                ...food,
                item_deleted_quantity: deletedQuantity,
              };
            }) || [];

          const updatedFoodsDamaged = order.foods
            .filter((food) => !restaurantId || food.restaurantId === restaurantId)
            .map((food) => {
              const { item_deleted_quantity, ...rest } = food;
              return rest;
            });

          await notifyAndUpdateStatus(orderOrigin, statusCancelled, updatedFoods);
          try {
            await sendReceipt(orderOrigin.id, 'cancel');
          } catch (e) {}
          await notifyAndUpdateStatus(order, statusDamagedCancelled, updatedFoodsDamaged);
          updateOrderStatusDamaged(order.parent_id, statusDamagedCancelled);
        }
      }

      toggleDrawer?.(false);
      toast.current = { content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' };
      setOpenToast(true);
      // navigate('/dashboard');
    } catch (error) {
      toast.current = { content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' };
      setOpenToast(true);
      console.error('Update order status failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className={classes.BoxButton}>
      {isLoading && (
        <Box className={classes.LoadingOverlay}>
          <CircularProgress color="inherit" />
        </Box>
      )}
      <Button
        id="close_button"
        fullWidth={false}
        boxStyle={{ width: 'auto' }}
        buttonStyle={buttonBackStyle}
        onClick={() => setCancellationOrder(false)}
        startIcon={<img src={ALIGN_LEFT_ICON} height={20} width={20} alt="ALIGN_LEFT_ICON" />}
      />
      <Button
        id="BUTTON_PARTIAL_CANCELLATION"
        fullWidth={false}
        title={t(LANG_TEXT.PARTIAL_CANCELLATION)}
        buttonStyle={{
          ...buttonStyle,
          background: 'var(--Primary-50, #F2FBF3) !important',
        }}
        sxTextStyle={{
          ...textButton,
          color: 'var(--Primary-700, #296C32) !important',
        }}
        onClick={() => {
          setCancellationOrder(false);
          setPartialCancellation(true);
        }}
      />
      <Button
        id="BUTTON_FULL_CANCELLATION"
        fullWidth={false}
        title={t(LANG_TEXT.FULL_CANCELLATION)}
        buttonStyle={{
          ...buttonStyle,
          background: 'var(--Primary-50, #F2FBF3) !important',
        }}
        sxTextStyle={{
          ...textButton,
          color: 'var(--Primary-700, #296C32) !important',
        }}
        onClick={() => handleOpenModalCancellation()}
      />
      <Modal
        open={openModalCancellation}
        handleClose={handleCloseModalCancellation}
        handleSubmit={() => handleConfirmFullCancellation(order!)}
        title={t(LANG_TEXT.FULL_CANCELLATION_TITLE)}
        description={t(LANG_TEXT.FULL_CANCELLATION_DESCRIPTION)}
      />
      <Toast
        content={toast.current?.content}
        variant={toast.current?.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
    </Box>
  );
};

export default OrderCancellation;
